<script lang="ts" setup>
import {
  defineOgImageComponent,
  useAsyncData,
  useHead,
  useI18n,
  useRoute,
  useStorage,
  useToast,
  watch,
  useSpots
} from '#imports';

import SpotView from '~~/app/blocks/SpotView/SpotView.vue';

const route = useRoute();
const { showError } = useToast();
const { t } = useI18n();
const { fetchSpotBySlug } = useSpots();
const { formatedImageUrl } = useStorage();

const emit = defineEmits([
  'close',
  'pauseTimer',
  'resumeTimer',
  'resetTimer',
  'next',
  'prev',
  'subscribe',

]);

let spotSlug = route.params.spotId as string || route.params.spotSlug as string;
const {
  data: spot,
  status: spotStatus,
  refresh: spotRefresh
} = await useAsyncData(`spotBySlug:{${spotSlug}}`, async () => {
  try {
    const data =  await fetchSpotBySlug(spotSlug)
    return data
  } catch (error) {
    showError(t('messages.spots.fetchSpot'));
  }
})

watch(route, async (newRoute) => {
  spotSlug = newRoute.params.spotId as string || newRoute.params.spotSlug as string;
  spotRefresh()
});

useHead(() => ({
  name: spot.value?.name,
  meta: [
    {
      name: 'description',
      content: spot.value?.summary,
    },
  ],
}));

defineOgImageComponent('Default', {
  name: spot.value?.name,
  description:
    spot.value?.summary?.split(' ').slice(0, 10).join(' ') +
    (spot.value?.summary.length > 90 ? '...' : ''),
  imagePath: formatedImageUrl(spot.value?.cover_image_url as string, 1200, 600, 75),
});
</script>
<template>
  <SpotView
    :spot="spot"
    @prev="emit('prev')"
    @next="emit('next')"
    @close="emit('close')"
    @pause-timer="emit('pauseTimer')"
    @resume-timer="emit('resumeTimer')"
    @reset-timer="emit('resetTimer')"
    @subscribe="emit('subscribe')"
  />
</template>

<style scoped lang="postcss">
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/typography.pcss";

.book-spot-details {
  display: grid;
  .spot-website-link {
    word-break: break-all;
  }
}

.book-spot-buttons {
  position: relative;
  display: grid;
  gap: 1rem;
  padding: 1 0;
  @mixin screen md {
    display: flex;
    position: absolute;
    left: 1rem;
    top: -2.5rem;
    padding: 1rem 0;

  }
}

.preview-overlay {
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
  border-radius: 1rem;
  max-height: 100%;
  min-height: 100%;
  padding: 1.5rem 1rem 1rem;
  @mixin screen md {
    padding: 1rem;
  }
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    background-size: auto 120%
  }
  2% {
    opacity: 1;
  }
  100% {
    background-size: auto 100%
  }
}

.fadeInOut {
  animation: fadeInOut 20s ease-in-out;
}
/*
@keyframes zoomPan {
  0% {
    background-size: auto 120%
  }
  100% {
    background-size: auto 100%
  }
}

.animate-zoom-pan {
  animation: zoomPan 20s ease-out forwards;
} */

.close-spot-button {
  color: black;
  border-radius: 50%;
  background-color: var(--color-slate-50);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  position: absolute;
  top: 0.5rem;
  left: 1rem;
  z-index: 1000;
  cursor: pointer;
  i {
    border-radius: 50%;
    font-size: 1rem;
    width: 1rem;
    height: 1rem;
    margin: -2px 0 0 -2px;
  }
}

.spot {
  isolation: isolate;
  z-index: 9999;
  position: relative;
  overflow-y: scroll;
  overflow-x: hidden;
  width: 100vw;
  height: 100vh;
  max-height:100%;
  text-align: center;
  color: var(--color-white);
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  .spot-container {
    position: relative;
  }

  .spot-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: fit-content;
    z-index: 99;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    margin-top: 0.5rem;
    color: var(--color-white);
    i {
      width: 1.625rem;
      height: 1.625rem;
      font-size: 1.625rem;
      background-color: transparent;
      z-index: 1000;
    }
  }
  .background {
    height: 100vh;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
  .spot-info {
    z-index: 0;
    width: 100vw;
    height: 100vh;
    z-index: 10;
  }
  .spot-info-wrappers {
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 20%, rgba(0, 0, 0, 0.60) 30%, rgba(0, 0, 0, 0.20) 50%, rgba(0, 0, 0, 1) 100%);


  }
}
h1 {
  font-size: 2.5rem;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin: 0 0 1rem;
}
.category {
  display: flex;
  align-content: center;
  margin-bottom: 2rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  i {

    font-size: 1rem;
    padding: 0.375rem;
    background-color: var(--color-indigo-400);
    border-radius: 100%;
  }

  @mixin screen md {
    color: var(--color-rose-500);
    @mixin typeface-summary;
    margin-bottom: 0.5rem;

    i {
      font-size: 1.5rem;
      background-color: transparent;
    }
  }
}
p {
  @mixin typeface-regular;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  margin-top: 0.5rem;
}
.spot-details {
  z-index: 10;
  text-align: center;
  position: relative;
  .trigger {
    display: grid;
    position: absolute;
    top: -5rem;
    left: 0;
    align-items: end;
    grid-template-rows: 1rem 1rem;
    gap: 1rem;
    width: 100%;
    background-color: transparent;
    color: var(--color-white);
    @mixin typeface-regular;
    i {
      font-size: 1.5rem;
    }

    @mixin screen md {
      display: none;
    }
  }
  .spot-details-content {
    margin-top: 0;
    min-height: 100vh;
    padding: 2rem 2rem 1rem;
    background-color: var(--main-background-color);
    color: var(--color-black);
    text-align: left;
    h1, .category {
      text-align: center;
    }
  }
  p {
    text-align: left;
  }
}

.spot-actions {
  display: flex;
  position: absolute;
  right: 1rem;
  top: 0.5rem;
  align-items: center;
  font-size: 1.75rem;
  gap: 0.75rem;
  @mixin screen md {
    font-size: 1.5rem;
    gap: 0.5rem;
  }
}

.spot-detail {
  line-height: 1.25;
  margin-bottom: 1rem;
  span {
    font-weight: 700;
  }
  a {
    @mixin typeface-regular;
    color: var(--color-black);
    text-decoration: underline;
  }
}

.preview-overlay + .spot-header + .spot-info {
  position: absolute;
  top: 0;
  z-index: -1;
}
.preview-overlay + .spot-header + .spot-info + .spot-details {
  display: none;
}

.map {
  width: 100%;
  height: 10rem;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-top: 1rem;
}

@mixin screen md {
  .preview-overlay + .spot-header + .spot-info {
    position: absolute;
    top: 0;
    z-index: -1;
  }
  .preview-overlay + .spot-header + .spot-info + .spot-details {
    display: none;
  }
  .spot {
    width: 100%;
    position: relative;
    max-height: 100%;
    height: fit-content;
    background-color: var(--main-background-color);
    padding: 0;
    /* border-radius: 0.75rem; */
    border: 1px solid var(--color-gray-100);
    .spot-header {
      width: 100%;
      height: 100%;
      z-index: 10;
      display: block;
      position: relative;
    }
    .background {
      height: 100vh;
      min-height: 18rem;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    .spot-info,
    .spot-header {
      display: block;
      position: relative;
      width: 100%;
      height: fit-content;
      z-index: 10;
    }
    .spot-header {
      position: absolute;
      z-index: 99;
      top: 0;
      width: 100%;
      display: flex;
    }
    .spot-info-wrappers {
      width: 100%;
      height: fit-content;
      text-align: left;

      background: transparent;
      text-align: center;
      h1 {
        margin-top: 2rem;
        @mixin typeface-h2;
      }
      p {
        display: none;
      }
    }
    .spot-details-content {
      padding-top: 1rem;
      display: grid;
      align-items: start;
      .map-container {
        display: none;
      }
    }
    .background {
      width: 100%;
      height: fit-content;
      position: relative;
    }
    .spot-details-content {
      min-height: fit-content;
    }
    .spot-details-content h1 {
      @mixin screen md {
        margin-top: 2rem;
        @mixin typeface-h2;
        text-align: left;
      }
    }
    .spot-details-content .category {
      @mixin screen md {
        text-align: left;
        justify-content: flex-start;
      }
    }
  }
}

.map {
  @mixin screen md {
    display: none;
  }
}

.mobile-bookmark {
  display: block;

  @mixin screen md {
    display: none
  }
}

.desktop-bookmark {
  display: none;

  @mixin screen md {
    display: block;
  }
}

.mobile-copyright {
  display: block;

  @mixin screen md {
    display: none
  }
}

.desktop-copyright {
  display: none;

  @mixin screen md {
    display: block;
  }
}

.spot-info-mobile {
  display: block;
  padding: 4rem 1rem;

  @mixin screen md {
    display: none;
  }
}

</style>
